import type { CSSProperties } from 'react';

import { ARROW_GAP_PX } from '@/components/Illustrations/arrowPlacement';

/**
 * The arrow image in its own pixels, read off its opaque pixels: its free end (the tail), the point of
 * its arrowhead, and the top of its loop, which is the drawing's highest point once tilted.
 */
export const ARROW_IMAGE = {
    width: 383,
    height: 122,
    tail: { x: 0, y: 120 },
    tip: { x: 373, y: 65.5 },
    crest: { x: 302, y: 4 },
} as const;

/** Width the arrow is drawn at, and the tilt it is drawn with, turned about its arrowhead. */
const ARROW_DRAWN_WIDTH_PX = 360;
const ARROW_TILT_DEG = -16;

interface Point {
    x: number;
    y: number;
}

/** Turns a vector clockwise by `degrees`, as CSS does on a y-down page. */
const turn = ({ x, y }: Point, degrees: number): Point => {
    const radians = (degrees * Math.PI) / 180;

    return {
        x: x * Math.cos(radians) - y * Math.sin(radians),
        y: x * Math.sin(radians) + y * Math.cos(radians),
    };
};

const TO_DRAWN = ARROW_DRAWN_WIDTH_PX / ARROW_IMAGE.width;

/** A point of the image in the drawn box, before the tilt. */
const drawn = ({ x, y }: Point): Point => ({ x: x * TO_DRAWN, y: y * TO_DRAWN });

/** Where a point of the image lands relative to the arrowhead, once drawn and tilted about it. */
const fromTip = (point: Point): Point => {
    const tip = drawn(ARROW_IMAGE.tip);
    const at = drawn(point);

    return turn({ x: at.x - tip.x, y: at.y - tip.y }, ARROW_TILT_DEG);
};

interface TranscriptArrowLayout {
    /** Placement of the drawn box, relative to the file panel's top-left corner. */
    arrowStyle: CSSProperties & { left: number; top: number };
    /** How far below the row's top the explanatory card starts, beside the arrow. */
    cardOffsetPx: number;
}

/**
 * The arrow hangs from the file panel: its tip stops `ARROW_GAP_PX` short of the panel's left edge, the
 * top of its loop is level with the panel's top, and the explanatory card starts `ARROW_GAP_PX` below
 * its tail. The arrow joins the two boxes without touching either, at every row width.
 */
const transcriptArrowLayout = (): TranscriptArrowLayout => {
    const tip = drawn(ARROW_IMAGE.tip);
    const tipTop = -fromTip(ARROW_IMAGE.crest).y;

    return {
        arrowStyle: {
            width: ARROW_DRAWN_WIDTH_PX,
            left: -(ARROW_GAP_PX + tip.x),
            top: tipTop - tip.y,
            transformOrigin: `${tip.x}px ${tip.y}px`,
            transform: `rotate(${ARROW_TILT_DEG}deg)`,
        },
        cardOffsetPx: tipTop + fromTip(ARROW_IMAGE.tail).y + ARROW_GAP_PX,
    };
};

export const TRANSCRIPT_ARROW_LAYOUT = transcriptArrowLayout();
